/**
 * Program listing — pure filter/sort/map helpers (no React)
 */
import { isIndiaCountry } from "@/helpers/countryCode";
import type { ProgramCardProps } from "@/components/atoms/ProgramCard/ProgramCard";
import type { ActiveFilters, FilterSection } from "@/components/atoms/ProgramFilters/ProgramFilters";
import {
  BASE_DOMAIN_OPTIONS,
  INDUSTRY_PARTNER_KEY_MAP,
  INDUSTRY_PARTNER_OPTIONS,
  PROGRAM_TYPE_OPTIONS,
  type Program,
} from "../model/programListing.types";

export const normalizeDuration = (s: string) =>
  s.trim().replace(/\b\w/g, (c) => c.toUpperCase());

export const getDuration = (p: Program) =>
  p.duration || p.attributes?.BannerSlider?.Duration?.text || "";

export const extractFeeValue = (p: Program, country: string): number | null => {
  if (p.fee) {
    const n = parseFloat(p.fee.replace(/[^0-9.]/g, ""));
    return isNaN(n) ? null : n;
  }
  const feeOptions = p.attributes?.Fees?.FeesOptions ?? [];
  const fullFee =
    feeOptions.find((f) => f.Type?.toLowerCase() === "full") ?? feeOptions[0];
  const raw = isIndiaCountry(country) ? fullFee?.FeesInInr : fullFee?.FeesInUSD;
  if (!raw) return null;
  const n = parseFloat(raw.replace(/[^0-9.]/g, ""));
  return isNaN(n) ? null : n;
};

export const durationSortKey = (s: string): [number, number] => {
  const lower = s.toLowerCase();
  const num = parseFloat(s.replace(/[^0-9.]/g, "")) || 0;
  if (lower.includes("year")) return [0, num];
  if (lower.includes("month")) return [1, num];
  if (lower.includes("hour")) return [2, num];
  return [3, num];
};

export const matchesDuration = (p: Program, durFilters: string[]) => {
  if (durFilters.length === 0) return true;
  const dur = normalizeDuration(getDuration(p)).toLowerCase();
  return durFilters.some((f) => dur === f.toLowerCase());
};

export function buildDomainOptions(certifications: Program[]): string[] {
  const seen = new Set<string>(BASE_DOMAIN_OPTIONS);
  certifications.forEach((c) => {
    const domains = Array.isArray(c.domain)
      ? c.domain
      : c.domain
        ? [c.domain]
        : [];
    domains.forEach((d) => seen.add(d.replace(/_/g, " ")));
  });
  return Array.from(seen);
}

export function buildDurationOptions(
  programs: Program[],
  certifications: Program[],
): string[] {
  const seen = new Map<string, string>();
  [...programs, ...certifications].forEach((p) => {
    const raw = getDuration(p);
    if (!raw) return;
    const display = normalizeDuration(raw);
    const key = display.toLowerCase();
    if (!seen.has(key)) seen.set(key, display);
  });
  return Array.from(seen.values()).sort((a, b) => {
    const [ga, na] = durationSortKey(a);
    const [gb, nb] = durationSortKey(b);
    return ga !== gb ? ga - gb : na - nb;
  });
}

export function buildFilterSections(
  hiddenProgramTypes: string[],
  domainOptions: string[],
  durationOptions: string[],
): FilterSection[] {
  const visibleProgramTypeOptions = PROGRAM_TYPE_OPTIONS.filter(
    (o) => !hiddenProgramTypes.includes(o),
  );
  return [
    {
      key: "programType",
      label: "Program Type",
      options: visibleProgramTypeOptions,
    },
    { key: "domain", label: "Domains", options: domainOptions },
    { key: "duration", label: "Duration", options: durationOptions },
    {
      key: "industryPartner",
      label: "Industry Partners",
      options: INDUSTRY_PARTNER_OPTIONS,
    },
  ];
}

export function filterCertifications(
  certifications: Program[],
  activeFilters: ActiveFilters,
  certPartnerFilters: string[],
): Program[] {
  const typeFilters = activeFilters.programType ?? [];
  if (typeFilters.length > 0 && !typeFilters.includes("Certifications")) {
    return [];
  }

  const domainFilters = activeFilters.domain ?? [];
  const durFilters = activeFilters.duration ?? [];
  const partnerFilters = activeFilters.industryPartner ?? [];

  return certifications.filter((c) => {
    if (domainFilters.length > 0) {
      const certDomains = (
        Array.isArray(c.domain) ? c.domain : c.domain ? [c.domain] : []
      ).map((d) => d.replace(/_/g, " ").toLowerCase());
      if (
        !domainFilters.some((f) =>
          certDomains.some((d) => d.includes(f.toLowerCase())),
        )
      ) {
        return false;
      }
    }
    if (
      certPartnerFilters.length > 0 &&
      !certPartnerFilters.includes(c.sponsoredBy ?? "")
    ) {
      return false;
    }
    if (
      partnerFilters.length > 0 &&
      !partnerFilters.includes(c.sponsoredBy ?? "")
    ) {
      return false;
    }
    return matchesDuration(c, durFilters);
  });
}

export function filterPrograms(
  programs: Program[],
  activeFilters: ActiveFilters,
): Program[] {
  return programs.filter((p) => {
    const type =
      p.programType || p.attributes?.BasicDetails?.ProgramType || "";
    const domain =
      p.domain ||
      p.attributes?.program_domain?.data?.attributes?.DomainHeading ||
      "";

    const typeFilters = activeFilters.programType ?? [];
    if (typeFilters.length > 0) {
      const typeMatch = typeFilters.some((f) =>
        type.toLowerCase().includes(f.replace(" Programs", "").toLowerCase()),
      );
      if (!typeMatch) return false;
    }

    const domainFilters = activeFilters.domain ?? [];
    if (domainFilters.length > 0) {
      const normalizedDomain = (
        typeof domain === "string" ? domain : ""
      )
        .replace(/_/g, " ")
        .toLowerCase();
      if (
        !domainFilters.some((f) => normalizedDomain.includes(f.toLowerCase()))
      ) {
        return false;
      }
    }

    if (!matchesDuration(p, activeFilters.duration ?? [])) return false;

    const partnerFilters = activeFilters.industryPartner ?? [];
    if (partnerFilters.length > 0) {
      const sponseredKey =
        p.sponseredKey ?? p.attributes?.BasicDetails?.SponsoredKey ?? null;
      const normalizedKey = sponseredKey ? sponseredKey.toLowerCase() : null;
      const partnerMatch = partnerFilters.some((label) => {
        const mappedKey = INDUSTRY_PARTNER_KEY_MAP[label];
        if (mappedKey === null) {
          return (p.sponsoredBy ?? "") === label;
        }
        return normalizedKey === mappedKey;
      });
      if (!partnerMatch) return false;
    }

    return true;
  });
}

export function sortPrograms(
  items: Program[],
  sortBy: string,
  country: string,
): Program[] {
  if (sortBy === "Fee: Low to High" || sortBy === "Fee: High to Low") {
    const dir = sortBy === "Fee: Low to High" ? 1 : -1;
    return [...items].sort((a, b) => {
      const fa = extractFeeValue(a, country);
      const fb = extractFeeValue(b, country);
      if (fa === null && fb === null) return 0;
      if (fa === null) return 1;
      if (fb === null) return -1;
      return (fa - fb) * dir;
    });
  }
  if (sortBy === "Most Popular" || sortBy === "Newest") {
    const tag = sortBy;
    return [...items].sort((a, b) => {
      const aHas = (a.sortBy ?? []).includes(tag) ? 0 : 1;
      const bHas = (b.sortBy ?? []).includes(tag) ? 0 : 1;
      return aHas - bHas;
    });
  }
  return items;
}

export function toProgramCardProps(
  p: Program,
  country: string,
  index: number,
): ProgramCardProps {
  const name =
    p.programName || p.attributes?.BasicDetails?.Heading || "Program";
  const dur = getDuration(p);

  const ShortSponsoredImage =
    p.attributes?.BasicDetails?.ShortSponsoredImage?.data?.attributes?.url ||
    p.attributes?.BasicDetails?.sponsoredLogoURL;

  const SponsoredKey =
    p.attributes?.BasicDetails?.SponsoredKey || undefined;

  const tags = Array.isArray(p.attributes?.BasicDetails?.Tags)
    ? p.attributes!.BasicDetails!.Tags!
    : [];
  const description =
    p.attributes?.BasicDetails?.CardShortDescription || p.description;

  const image =
    p.image ||
    p.attributes?.BasicDetails?.Image?.source?.data?.[0]?.attributes?.url;

  const href =
    p.href ||
    p.attributes?.BasicDetails?.url_key ||
    (p.attributes?.slug ? `/${p.attributes.slug}` : "#");

  const feeOptions = p.attributes?.Fees?.FeesOptions ?? [];
  const fullFee =
    feeOptions.find((f) => f.Type?.toLowerCase() === "full") ?? feeOptions[0];
  const fee =
    p.fee ||
    (isIndiaCountry(country) ? fullFee?.FeesInInr : fullFee?.FeesInUSD) ||
    undefined;

  const courseStripText =
    p.attributes?.BasicDetails?.CourseStripText || undefined;

  return {
    image,
    partnerName: p.partnerName,
    title: name,
    description,
    tags: tags ?? [],
    duration: dur,
    fee,
    href,
    courseStripText,
    ShortSponsoredImage,
    SponsoredKey,
    priority: index < 2,
  };
}

export function buildOptionSelected(
  filters: ActiveFilters,
  certPartners: string[],
) {
  const all = [...Object.values(filters).flat(), ...certPartners].filter(
    (v, i, a) => a.indexOf(v) === i,
  );
  return all.length > 0 ? all.join(" | ") : "NA";
}
